import React, { useState } from 'react';
import { Box, CardContent, Stack, Alert, Tabs, Tab } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useGetWriteoffQuery } from '../../../services/WriteoffService';
import { WriteoffDetailsSection, NotesSection, DocumentsSection, ApprovalsSection, PatientInfoView, WriteoffEditHeader } from '../components';
import PageCard from '../../common/components/styled-ui-elements/PageCard';
import { PageLoading as Loading } from '../../common/components';

// Constants
const PAGE_STYLES = {
  container: {
    minHeight: '100vh',
    py: 1.5,
    px: 2,
  }
} as const;

// Tab Panel Interface and Component
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const CustomTabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`writeoff-tabpanel-${index}`}
    aria-labelledby={`writeoff-tab-${index}`}
  >
    {value === index && (
      <Box sx={{ pt: 3, pb: 1.5, px: 1 }}>
        {children}
      </Box>
    )}
  </div>
);

// Types
interface WriteoffEditParams {
  company: string | null;
  center: string | null;
  id: string | null;
  accountId: string | null;
}

// Custom hooks
const useWriteoffEditParams = (): WriteoffEditParams => {
  const [searchParams] = useSearchParams();
  return {
    company: searchParams.get('company'),
    center: searchParams.get('center'),
    id: searchParams.get('id'),
    accountId: searchParams.get('accountId')
  };
};

// Error State Component
const ErrorState: React.FC<{ message: string; onBack: () => void }> = ({ message, onBack }) => (
  <Box sx={{ p: 3 }}>
    <Alert severity="error">{message}</Alert>
    <Box sx={{ mt: 2 }}>
      <button onClick={onBack}>Back to Writeoffs</button>
    </Box>
  </Box>
);

// Main component
export const WriteoffEdit: React.FC = () => {
  const navigate = useNavigate();
  const params = useWriteoffEditParams();
  const { company, center, id } = params;
  const hasRequiredParams = Boolean(id);

  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);

  // Fetch writeoff data - Redux state is auto-populated via extraReducers
  const { 
    isLoading, 
    error: queryError,
  } = useGetWriteoffQuery(id!, {
    skip: !hasRequiredParams,
  });

  const handleBack = () => {
    // Try to go back in browser history first
    // This preserves the worklist/dashboard the user came from
    window.history.back();
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Early returns for error states
  if (!hasRequiredParams) {
    return (
      <ErrorState
        message={`Missing required parameters. Expected: id. Current: id=${id || 'missing'}`}
        onBack={handleBack}
      />
    );
  }

  if (isLoading) {
    return <Loading />;
  }

  if (queryError) {
    return (
      <ErrorState
        message="Failed to load writeoff request. Please try again."
        onBack={handleBack}
      />
    );
  }

  // Main render - all components read from Redux state
  return (
    <Box sx={PAGE_STYLES.container}>
      <Box display="flex" justifyContent="center">
        <PageCard>
          <WriteoffEditHeader
            onBack={handleBack}
            onError={setError}
          />
          
          <CardContent sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            <Stack spacing={3}>
              <PatientInfoView />
              
              <WriteoffDetailsSection />
              
              {/* Tab Strip Section */}
              <Box>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                  <Tabs 
                    value={tabValue} 
                    onChange={handleTabChange} 
                    aria-label="writeoff tabs"
                    sx={{
                      minHeight: '40px',
                      '& .MuiTab-root': {
                        minHeight: '40px',
                        padding: '6px 16px',
                        fontSize: '0.875rem'
                      }
                    }}
                  >
                    <Tab label="Notes" />
                    <Tab label="Documents" />
                    <Tab label="Approvals" />
                  </Tabs>
                </Box>
                
                <CustomTabPanel value={tabValue} index={0}>
                  <NotesSection />
                </CustomTabPanel>
                
                <CustomTabPanel value={tabValue} index={1}>
                  <DocumentsSection />
                </CustomTabPanel>
                
                <CustomTabPanel value={tabValue} index={2}>
                  <ApprovalsSection />
                </CustomTabPanel>
              </Box>
            </Stack>
          </CardContent>
        </PageCard>
      </Box>
    </Box>
  );
};

export default WriteoffEdit;
